import {
    ModuleRegistry,
    AllCommunityModule,
    CellClickedEvent,
    CellKeyDownEvent,
    FirstDataRenderedEvent,
    CellFocusedEvent,
} from "ag-grid-community";
import { ColumnMenuModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useRef, useMemo, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
    currentDraftAtom,
    isReadonlyAtom,
    labelingParamsAtom,
    onRowAccountEditAtom,
    showReadableLabelAtom,
    tableRowsAtom,
} from "../../state";
import { labelingTableTheme } from "./labelingTableTheme";
import {
    AccountLabelCellRenderer,
    AccountLabelColDef,
    AccountNameCellRenderer,
    AccountNameColDef,
    PeriodColDef,
    RowHeaderColDef,
} from "./columnDefinitions";
import { useWindowSize, useWindowSizeListener } from "@/ui/hooks/useWindowSize";

ModuleRegistry.registerModules([AllCommunityModule, ColumnMenuModule]);

export function FileView() {
    const { selectedFileId } = useAtomValue(labelingParamsAtom);
    const isReadonly = useAtomValue(isReadonlyAtom);
    const { isLoading, data: draft } = useAtomValue(currentDraftAtom);
    const gridRef = useRef<AgGridReact>(null);
    const showReadableLabel = useAtomValue(showReadableLabelAtom);
    const rowData = useAtomValue(tableRowsAtom);
    const onRowAccountEdit = useSetAtom(onRowAccountEditAtom);
    useWindowSizeListener();
    const windowSize = useWindowSize();
    const isLargeScreen = windowSize.width > 1400;
    const prevFocusedRowIndex = useRef<number | null>(null);

    const handleCellFocused = useCallback((event: CellFocusedEvent) => {
        if (event.rowIndex === prevFocusedRowIndex.current) return;

        const container = document.getElementById("labeling-grid");
        if (!container) return;

        if (prevFocusedRowIndex.current !== null) {
            const prevRows = container.querySelectorAll(
                `[row-index="${prevFocusedRowIndex.current}"]`,
            );
            prevRows.forEach((row) => row.classList.remove("ag-row-focused"));
        }

        if (event.rowIndex !== null) {
            const newRows = container.querySelectorAll(
                `[row-index="${event.rowIndex}"]`,
            );
            newRows.forEach((row) => row.classList.add("ag-row-focused"));
        }

        prevFocusedRowIndex.current = event.rowIndex;
    }, []);

    const columnDefs = useMemo(
        () => [
            {
                ...AccountLabelColDef,
                cellRenderer: (params) => (
                    <AccountLabelCellRenderer
                        label={params.data.account?.label}
                        showReadableLabel={showReadableLabel}
                        isDisabled={params.data.isDisabled}
                        isTentative={params.data.isTentative}
                    />
                ),
            },
            {
                ...AccountNameColDef,
                cellRenderer: (params) => (
                    <AccountNameCellRenderer
                        accountName={params.data.account?.accountName}
                        isDisabled={params.data.isDisabled}
                        isTentative={params.data.isTentative}
                    />
                ),
                editable: false,
            },
            {
                ...RowHeaderColDef,
                pinned: isLargeScreen ? ("left" as const) : undefined,
            },

            ...(draft?.periods?.map((period) => PeriodColDef({ period })) ||
                []),
        ],
        [draft?.periods, showReadableLabel, isLargeScreen],
    );

    const handleFirstDataRendered = useCallback(
        (event: FirstDataRenderedEvent) => {
            const columns = event.api.getAllDisplayedColumns();
            const firstCol = columns?.[0];
            if (firstCol) {
                event.api.setFocusedCell(0, firstCol);
            }
        },
        [],
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const handleCellKeyDown = useCallback(
        (event: CellKeyDownEvent) => {
            const keyboardEvent = event.event as KeyboardEvent;
            if (keyboardEvent.key === "Enter" && event.data) {
                onRowAccountEdit({
                    colId: event.colDef.colId,
                    rowIndex: event.data.rowIndex,
                    account: event.data.account,
                    rowHeader: event.data.rowHeader,
                    isDisabled: event.data.isDisabled,
                });
            }
        },
        [onRowAccountEdit],
    );

    if (!selectedFileId) return null;

    if (!draft && isLoading)
        return (
            <div className="size-full">
                <div className="bg-selected size-full animate-pulse rounded-xl" />
            </div>
        );

    return (
        <div
            id="labeling-grid"
            ref={containerRef}
            tabIndex={0}
            className="size-full outline-none"
        >
            <AgGridReact
                key={`${selectedFileId}-${isReadonly}-${isLargeScreen}-${draft?.id}`}
                ref={gridRef}
                theme={labelingTableTheme}
                rowData={rowData}
                loading={isLoading}
                getRowId={(params) => params.data.rowIndex}
                onFirstDataRendered={handleFirstDataRendered}
                onCellKeyDown={handleCellKeyDown}
                onCellFocused={handleCellFocused}
                onCellClicked={(event: CellClickedEvent) =>
                    onRowAccountEdit({
                        colId: event.colDef.colId,
                        rowIndex: event.data.rowIndex,
                        account: event.data.account,
                        rowHeader: event.data.rowHeader,
                        isDisabled: event.data.isDisabled,
                    })
                }
                columnDefs={columnDefs}
                defaultColDef={{
                    flex: 1,
                    cellClass: "no-focus-border",
                }}
                suppressHeaderFocus
                rowClassRules={{
                    "ag-row-disabled": (params) => params.data.isDisabled,
                    "ag-row-selected-border": (params) =>
                        params.data.isSelected,
                }}
                rowSelection={{
                    mode: "multiRow",
                    checkboxes: false,
                    headerCheckbox: false,
                    enableClickSelection: true,
                }}
            />
        </div>
    );
}
